/**
 * Reads a ds-theme motion duration token at call time, so JS-side teardown timing
 * can never drift from the CSS transition it waits for.
 */
export function motionDurationMs(token: '--t-fast' | '--t-mid' | '--t-slow' | '--t-fly', element?: Element): number {
  if (typeof window === 'undefined') return 0;
  const raw = getComputedStyle(element ?? document.documentElement)
    .getPropertyValue(token)
    .trim();
  if (raw.endsWith('ms')) return parseFloat(raw);
  if (raw.endsWith('s')) return parseFloat(raw) * 1000;
  return parseFloat(raw) || 0;
}

/**
 * Accordion for any container whose content changes: measures, applies the change, then plays the
 * height delta on the ds-theme motion tokens. `apply` must leave the new content laid out by the time
 * it returns (in Angular: write the signal, then flush with `ChangeDetectorRef.detectChanges()`), so
 * the closing measurement is the real one. `prefers-reduced-motion: reduce` applies without animating.
 *
 * Returns the running animation, or `undefined` when none was needed — while it runs the element still
 * occupies the OLD height, so anything that depends on the settled layout (scrolling the section into
 * view) must wait on `finished` rather than fire alongside the call.
 */
export function animateHeight(el: HTMLElement, apply: () => void): Animation | undefined {
  const from = el.getBoundingClientRect().height;
  apply();
  if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
  const to = el.getBoundingClientRect().height;
  if (from === to) return undefined;

  const easing = getComputedStyle(el).getPropertyValue('--ease-smooth').trim() || 'ease';
  el.style.overflow = 'hidden';
  const animation = el.animate([{ height: `${from}px` }, { height: `${to}px` }], {
    duration: motionDurationMs('--t-mid', el),
    easing,
  });
  // Both endings restore the element: a second toggle mid-flight cancels this one.
  const restore = (): void => {
    el.style.overflow = '';
  };

  animation.onfinish = restore;
  animation.oncancel = restore;
  return animation;
}
